'use client'

import { useState, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Clock, Type } from 'lucide-react'
import { countWords } from '@/components/tools/word-counter'
import { overallWpm } from '@/lib/seo/wpm-data'

// Speaking-pace presets for the calculator (words per minute). The 110/140/170
// presets are round conversion shortcuts. The measured presets are the 25th
// percentile, median, and 75th percentile of the delivery study in
// lib/seo/wpm-data.ts, carried here when the "how many words in an N minute
// script" pages were merged into this tool.
const PACE_PRESETS = [
  { label: 'Slow', sub: '110 wpm', value: 110 },
  { label: 'Medium', sub: '140 wpm', value: 140 },
  { label: 'Fast', sub: '170 wpm', value: 170 },
  { label: 'Measured 25th', sub: `${overallWpm.p25} wpm`, value: overallWpm.p25 },
  { label: 'Measured median', sub: `${overallWpm.median} wpm`, value: overallWpm.median },
  { label: 'Measured 75th', sub: `${overallWpm.p75} wpm`, value: overallWpm.p75 },
]

const REFERENCE_MINUTES = [1, 3, 5, 8, 10, 15, 20]

function formatMinSec(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.round(totalSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

type Mode = 'wordsToTime' | 'timeToWords' | 'pasteScript'

interface WordsToMinutesProps {
  compact?: boolean
  /**
   * The script pasted in the "Paste script" mode. The calculator page's own
   * client wrapper owns it so the free check below the tool runs on the same
   * draft; the embed page, which has no check to feed, leaves both props out
   * and the mode keeps its own state.
   */
  script?: string
  onScriptChange?: (value: string) => void
}

export function WordsToMinutes({ compact = false, script, onScriptChange }: WordsToMinutesProps) {
  const [mode, setMode] = useState<Mode>('wordsToTime')
  const [wpm, setWpm] = useState(140)
  const [words, setWords] = useState('1000')
  const [minutes, setMinutes] = useState('7')
  const [ownScript, setOwnScript] = useState('')
  const pastedText = script ?? ownScript
  const setPastedText = onScriptChange ?? setOwnScript

  // Read through the word counter's own counter, so the number here and the
  // number on /tools/script-word-counter are the same number for the same text.
  const pastedWords = countWords(pastedText)

  const result = useMemo(() => {
    if (mode === 'wordsToTime') {
      const w = Math.max(0, parseInt(words) || 0)
      const seconds = (w / wpm) * 60
      return { primary: formatMinSec(seconds), label: 'Speaking time', sub: `${w.toLocaleString()} words at ${wpm} wpm` }
    }
    if (mode === 'timeToWords') {
      const m = Math.max(0, parseFloat(minutes) || 0)
      const w = Math.round(m * wpm)
      return { primary: w.toLocaleString(), label: 'Words needed', sub: `${m} min at ${wpm} wpm` }
    }
    const seconds = (pastedWords / wpm) * 60
    return {
      primary: formatMinSec(seconds),
      label: 'Speaking time',
      sub: `${pastedWords.toLocaleString()} words at ${wpm} wpm`,
    }
  }, [mode, wpm, words, minutes, pastedWords])

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {/* Mode toggle */}
      <div className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-card p-1">
        <button
          onClick={() => setMode('wordsToTime')}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            mode === 'wordsToTime' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Words → Minutes
        </button>
        <button
          onClick={() => setMode('timeToWords')}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            mode === 'timeToWords' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Minutes → Words
        </button>
        <button
          onClick={() => setMode('pasteScript')}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            mode === 'pasteScript' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Paste script
        </button>
      </div>

      {/* Input */}
      {mode === 'pasteScript' ? (
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">Paste your script</label>
          <textarea
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Paste your script here..."
            className="h-36 w-full resize-y rounded-md border border-border bg-background px-4 py-3 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <p className="mt-1 text-xs text-muted-foreground">{pastedWords.toLocaleString()} words</p>
        </div>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">
            {mode === 'wordsToTime' ? 'Word count' : 'Video length (minutes)'}
          </label>
          {mode === 'wordsToTime' ? (
            <div className="relative">
              <Type className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="number"
                inputMode="numeric"
                value={words}
                onChange={(e) => setWords(e.target.value)}
                min={0}
                className="w-full rounded-md border border-border bg-background py-3 pl-10 pr-4 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="e.g. 1000"
              />
            </div>
          ) : (
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="number"
                inputMode="decimal"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                min={0}
                step={0.5}
                className="w-full rounded-md border border-border bg-background py-3 pl-10 pr-4 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="e.g. 7"
              />
            </div>
          )}
        </div>
      )}

      {/* Pace selector */}
      <div>
        <span className="mb-2 block text-sm font-medium text-foreground">Speaking pace</span>
        <div className="grid grid-cols-3 gap-2">
          {PACE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => setWpm(preset.value)}
              className={`rounded-md border px-3 py-2 text-center transition-colors ${
                wpm === preset.value
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-background hover:border-primary/50'
              }`}
            >
              <span className={`block text-sm font-semibold ${wpm === preset.value ? 'text-primary' : 'text-foreground'}`}>
                {preset.label}
              </span>
              <span className="block text-xs text-muted-foreground">{preset.sub}</span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          The measured presets are the 25th percentile, median, and 75th percentile of the {overallWpm.n} videos we timed, not round planning numbers.
        </p>
      </div>

      {/* Result */}
      <Card className="border-primary/30">
        <CardContent className={`${compact ? 'p-4' : 'p-6'} text-center`}>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{result.label}</div>
          <div className={`${compact ? 'text-3xl' : 'text-4xl'} mt-1 font-bold text-primary tabular-nums`}>{result.primary}</div>
          <div className="mt-1 text-xs text-muted-foreground">{result.sub}</div>
        </CardContent>
      </Card>

      {/* Reference table */}
      {!compact && <div>
        <h3 className="mb-3 text-center text-sm font-semibold text-foreground">Script length reference</h3>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-card text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Video length</th>
                <th className="px-3 py-2 text-right font-medium">Slow<span className="hidden sm:inline"> (110)</span></th>
                <th className="px-3 py-2 text-right font-medium">Avg<span className="hidden sm:inline"> (140)</span></th>
                <th className="px-3 py-2 text-right font-medium">Fast<span className="hidden sm:inline"> (170)</span></th>
              </tr>
            </thead>
            <tbody>
              {REFERENCE_MINUTES.map((m) => (
                <tr key={m} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-medium text-foreground">{m} min</td>
                  <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{(m * 110).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-foreground tabular-nums">{(m * 140).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{(m * 170).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-center text-xs text-muted-foreground">Spoken-word time only. Add your own edit allowance for pauses, B-roll, and visuals.</p>
      </div>}
    </div>
  )
}
